import React, { useEffect, useRef, useState } from 'react'
import styles from '../WagesPage.module.css'
import o from '../../components/Overlay.module.css'
import wo from './overlays.module.css'
import { useLayer } from '../../components/layers'
import { apiJson } from '../../lib/api'
import { PendingButton, useSlow, inertClass } from '../../components/pending'
import { fmtDur, fmtStamp } from '../timesheets/time'
import { SCOPE_LABEL, blockedLabel, clock, dmy, hrs, kg, money, plural, rangeLabel } from './format'
import type { BasisDay, BasisRow, DetailResponse, QueueItem, Scope, WageGroup } from './types'

// The drill-down (V4 wgDrawer): what the number is, and the work that produced
// it. Figures come from GET /api/wages/detail; the drawer arranges them.

interface Props {
  user: any
  group: WageGroup
  scope: Scope
  from: string
  to: string
  queue: QueueItem[]
  onClose: () => void
  onQueued: (msg: string) => void
}

type Tab = 'calc' | 'sessions' | 'basis'

export function WageDrawer({ user, group, scope, from, to, queue, onClose, onQueued }: Props) {
  const [tab, setTab] = useState<Tab>('calc')
  const [detail, setDetail] = useState<DetailResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const userRef = useRef(user)
  userRef.current = user
  const slow = useSlow(busy)
  // A layer (h 425-458): Escape and the scrim close it. Nothing closes it
  // while a requeue is out (h 3984-3992).
  const drawerEl = useRef<HTMLDivElement>(null)
  const { requestClose, confirm } = useLayer(true, onClose, { el: drawerEl, modal: true, busy })

  useEffect(() => {
    let live = true
    setDetail(null)
    setError(null)
    const qs = new URLSearchParams({ key: group.key, mode: scope, from, to })
    apiJson<DetailResponse>(userRef.current, `/api/wages/detail?${qs}`)
      .then((d) => live && setDetail(d))
      .catch((e) => live && setError(e?.message || 'Could not load the calculation'))
    return () => {
      live = false
    }
  }, [group.key, scope, from, to])

  // The list row may be a moment older than the detail; the detail wins.
  const g = detail?.group || group
  const rows = detail?.rows || []
  const days = detail?.days || []
  const dayOf = new Map(days.map((d) => [d.work_date, d]))
  const partOf = new Map((g.break_parts || []).map((p) => [p.team_id, p]))
  const staleDay = (d: string) => queue.some((q) => q.human_id === g.human_id && q.work_date === d)
  const period = g.work_date ? dmy(g.work_date) : rangeLabel(from, to)
  const team = g.team_name || plural(g.teams.length, 'team', 'teams')

  const explain =
    scope === 'daily'
      ? `Scope: worker + work date + team. ${g.worker_name}’s ${g.team_name} work on ${dmy(g.work_date)} is assessed on its own, so a weak day is topped up even if the week clears the minimum.`
      : scope === 'team'
        ? `Scope: worker + team across ${rangeLabel(from, to)}. All ${g.team_name} sessions are combined before the minimum is assessed; other teams are assessed separately.`
        : `Scope: worker across ${rangeLabel(from, to)}. Every qualifying session is combined before the minimum is assessed. Changing the period can legitimately change this top-up.`

  const queueThis = async () => {
    setBusy(true)
    try {
      const dates = g.dates.slice().sort()
      const r = await apiJson<{ queued: number; skipped_locked: unknown[] }>(userRef.current, '/api/wages/recalculate', {
        body: { human_ids: [g.human_id], from: dates[0], to: dates[dates.length - 1], reason: 'Requested from the wage drill-down' },
      })
      onQueued(`${g.worker_name} queued for ${plural(r.queued, 'day', 'days')}` + (r.skipped_locked?.length ? ` · ${r.skipped_locked.length} locked day(s) left as paid` : ''))
    } catch (e: any) {
      onQueued(e?.message || 'Could not queue the recalculation')
    } finally {
      setBusy(false)
    }
  }

  const breakMin = g.paid_break_minutes || 0
  const bp = g.break_parts || []
  const breakNote =
    bp.length === 1 ? bp[0].sentence : bp.length ? 'Priced per team at the higher of minimum and effective piece rate' : ''

  const lines: { label: string; value: string; note?: string; strong?: boolean; big?: boolean; accent?: boolean }[] = g.blocked
    ? []
    : [
        { label: 'Hours included', value: hrs(g.paid_hours), note: breakMin ? 'includes ' + hrs(breakMin / 60) + ' paid break' : '' },
        { label: 'Minimum hourly rate', value: money(g.min_rate) },
        {
          label: 'Minimum required pay',
          value: money(g.min_required),
          strong: true,
          note: g.min_required_exact ? `Exactly $${g.min_required_exact} before rounding to the cent` : '',
        },
        { label: 'Piece-rate earnings', value: money(g.piece) },
        { label: 'Hourly earnings', value: money(g.hourly) },
        { label: 'Paid breaks', value: money(g.breaks), note: breakNote },
        {
          label: 'Earnings',
          value: money(g.earned),
          strong: true,
          note: g.earned_exact ? `Exactly $${g.earned_exact} before rounding. The parts above add up to this; a spare cent from rounding sits in the part with the largest fraction, never in the top-up.` : '',
        },
        {
          label: 'Top-up',
          value: money(g.top_up),
          accent: (g.top_up || 0) > 0,
          note: scope !== 'daily' && g.assessment === 'per_day' ? 'Assessed per day, then summed' : '',
        },
        { label: 'Final payable', value: money(g.wages), strong: true, big: true },
      ]

  return (
    <>
      <div className={o.drawerScrim} onClick={(e) => e.target === e.currentTarget && requestClose()}>
      <aside ref={drawerEl} tabIndex={-1} className={`${o.drawer} ${wo.wDrawer}`} role="dialog" aria-label={`Wages for ${g.worker_name}`}>
        <div className={`${o.drawerHead} ${wo.wHead}`}>
          <div className={wo.wTitleRow}>
            <div className={wo.wTitleText}>
              <h2 className={`${o.drawerTitle} ${wo.wTitle}`}>{g.worker_name}</h2>
              <div className={wo.wMeta}>
                {SCOPE_LABEL[scope]} · {period} · {team}
                {g.employee_id ? ' · ' + g.employee_id : ''}
              </div>
            </div>
            <div className={wo.wHeadEnd}>
              {g.stale && <span className={`${wo.chip} ${wo.chipQueued}`}>Recalculating</span>}
              <button type="button" className={`${o.close} ${wo.qClose}`} aria-label="Close" onClick={requestClose} disabled={busy}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.75" strokeLinecap="round" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12" /></svg>
              </button>
            </div>
          </div>
          {g.blocked ? (
            <div className={styles.bad} data-testid="drawer-blocked">
              <strong>{blockedLabel(g)}</strong>
              {g.blocked_reasons.map((r) => (
                <div key={r.work_date} style={{ marginTop: 6 }}>
                  {dmy(r.work_date)}: {r.message}
                </div>
              ))}
              <div style={{ marginTop: 6 }}>No hours or wage figure is shown for this row, and it is left out of every total and export.</div>
            </div>
          ) : (
            <div className={styles.tiles}>
              <div className={styles.tile}>
                <div className={styles.tileLabel}>Hours</div>
                <div className={styles.tileValue}>{hrs(g.paid_hours)}</div>
                <div className={styles.tileNote}>{breakMin ? `incl. ${breakMin} min paid break` : 'no paid break'}</div>
              </div>
              <div className={styles.tile}>
                <div className={styles.tileLabel}>Earned</div>
                <div className={styles.tileValue}>{money(g.earned)}</div>
              </div>
              <div className={styles.tile}>
                <div className={styles.tileLabel}>Top-up</div>
                <div className={styles.tileValue}>{g.top_up ? money(g.top_up) : 'None required'}</div>
              </div>
              <div className={styles.tile}>
                <div className={styles.tileLabel}>Wages</div>
                <div className={styles.tileValue}>{money(g.wages)}</div>
              </div>
            </div>
          )}

          <div className={wo.wExplain}>{explain}</div>
        </div>

          <div className={wo.wTabs} role="tablist">
            {(
              [
                ['calc', 'Calculation'],
                ['sessions', 'Contributing sessions'],
                ['basis', 'Wage basis'],
              ] as [Tab, string][]
            ).map(([k, label]) => (
              <button key={k} type="button" role="tab" aria-selected={tab === k} className={tab === k ? `${wo.wTab} ${wo.wTabOn}` : wo.wTab} onClick={() => setTab(k)}>
                {label}
              </button>
            ))}
          </div>

        <div className={`${wo.wBody} ${inertClass(busy, slow)}`}>
          {g.rounding === 'per_component' && !g.blocked && (
            <div className={styles.muted}>
              Calculated under {g.calculation_version}, before amounts were carried at full precision and rounded once. It is shown as it was calculated.
            </div>
          )}
          {error && <div className={o.danger}>{error}</div>}
          {!detail && !error && <div className={wo.hint}>Loading the calculation…</div>}

          {tab === 'calc' && (
            <div>
              {g.blocked && <div className={styles.muted}>There is no calculation for this row until the block above is resolved.</div>}
              {lines.map((l) => (
                <div key={l.label} className={[styles.calcLine, l.strong ? styles.calcStrong : '', l.big ? styles.calcBig : ''].join(' ')}>
                  <div className={styles.calcLabel}>
                    {l.label}
                    {l.note && <div className={styles.calcNote}>{l.note}</div>}
                  </div>
                  <div className={[styles.calcValue, l.accent ? styles.calcAccent : ''].join(' ')}>{l.value}</div>
                </div>
              ))}
              {scope === 'combined' && (g.made_up_of?.length || 0) > 1 && (
                <div className={styles.madeUp}>
                  <div className={styles.sectionTitle}>Made up of</div>
                  {g.made_up_of!.map((t) => (
                    <div key={t.team_id} className={styles.madeRow}>
                      <span>{t.team_name}</span>
                      <span>{t.paid_hours == null ? '—' : hrs(t.paid_hours)}</span>
                      <span>{t.earned == null ? '—' : money(t.earned)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {tab === 'sessions' && detail && (
            <div className={styles.sessList}>
              {rows.length === 0 && <div className={styles.muted}>No sessions.</div>}
              {rows.map((r) => (
                <SessionRow key={r.id} r={r} rate={partOf.get(r.team_id)?.rate} amounts={g.session_amounts?.[r.session_id]} />
              ))}
            </div>
          )}

          {tab === 'basis' && detail && (
            <div className={styles.basisList}>
              {days.map((d) => (
                <DayFacts key={d.work_date} d={d} />
              ))}
              {rows.map((r) => (
                <BasisCard key={r.id} r={r} day={dayOf.get(r.work_date)} part={partOf.get(r.team_id)} stale={staleDay(r.work_date)} amounts={g.session_amounts?.[r.session_id]} />
              ))}
              <PendingButton type="button" className={`${o.ghost} ${wo.wQueueBtn}`} disabled={false} busy={busy} pending="Queuing…" onClick={queueThis}>
                Queue this worker for recalculation
              </PendingButton>
              <div className={styles.muted}>Only this worker’s affected dates are rebuilt. The previous valid basis stays in use until the new one is complete.</div>
            </div>
          )}
        </div>
      </aside>
      </div>
      {confirm}
    </>
  )
}

type Amounts = { piece: number; hourly: number }

// Per-session amounts are the server's reconciled cents when the group was
// calculated at full precision; otherwise the amounts as stored on the row.
const pieceOf = (r: BasisRow, a?: Amounts) => (a ? a.piece : Number(r.piece_earnings) || 0)
const hourlyOf = (r: BasisRow, a?: Amounts) => (a ? a.hourly : Number(r.hourly_earnings) || 0)

function SessionRow({ r, rate, amounts }: { r: BasisRow; rate?: number; amounts?: Amounts }) {
  const earned = pieceOf(r, amounts) + hourlyOf(r, amounts)
  const h = r.worked_minutes / 60
  return (
    <div className={styles.sessRow}>
      <div>
        <div className={styles.sessMain}>
          {dmy(r.work_date)} · {clock(r.start_at, r.work_date)}–{r.is_open ? 'open' : clock(r.finish_at, r.work_date)}
        </div>
        <div className={styles.sub}>
          {r.team_name}
          {r.work_type ? ' · ' + r.work_type : ''}
        </div>
      </div>
      <div className={styles.num}>{hrs(h)}</div>
      <div className={styles.num}>{kg(r.qty_kg)}</div>
      <div className={styles.num}>{h && earned ? money(earned / h) + '/h' : '—'}</div>
      <div className={styles.num}>
        <strong>{money(earned)}</strong>
        {r.paid_break_minutes > 0 && rate != null && (
          <div className={styles.sub}>
            + {r.paid_break_minutes} min paid break at {money(rate)}/h
          </div>
        )}
      </div>
    </div>
  )
}

// The day's canonical time (shift-math): recorded time less the unpaid break
// not already covered by a gap. Minutes, labelled as such.
function DayFacts({ d }: { d: BasisDay }) {
  // D6.13: a spring crossing paid from the manager-entered normal hours. The note says why net payable
  // differs from the recorded time; the calculation wrote it, so it is shown as written.
  const applied = (d.notes || []).find((n) => n.code === 'NORMAL_HOURS_APPLIED')
  return (
    <div className={styles.dayFacts}>
      <strong>{dmy(d.work_date)}</strong> · recorded {fmtDur(d.recorded_minutes)} · paid break {d.paid_break_minutes} min · unpaid break {d.unpaid_break_minutes} min
      {d.gap_minutes ? ` (gap ${d.gap_minutes} min)` : ''} · deducted {d.unpaid_deduction_minutes} min · <strong>net payable {fmtDur(d.net_payable_minutes)}</strong>
      {applied && (
        <div className={styles.basisWhy} data-testid="day-normal-hours">
          {applied.text}
        </div>
      )}
    </div>
  )
}

function BasisCard({ r, day, part, stale, amounts }: { r: BasisRow; day?: BasisDay; part?: { rate: number; sentence: string }; stale: boolean; amounts?: Amounts }) {
  const h = r.worked_minutes / 60
  const piece = pieceOf(r, amounts)
  const line =
    hrs(h) +
    ' · ' +
    (r.qty_kg ? kg(r.qty_kg) + ' · ' : '') +
    (r.piece_earnings ? money(piece) + ' piece' : money(hourlyOf(r, amounts)) + ' hourly') +
    (r.paid_break_minutes && part ? ` · ${r.paid_break_minutes} min paid break at ${money(part.rate)}/h` : '')
  const rates =
    (r.pay_basis === 'hourly' ? money(r.base_rate) + '/h' : 'piece rate') +
    ' · min ' +
    money(r.min_rate) +
    '/h' +
    (h && r.piece_earnings ? ' · works out at ' + money(piece / h) + '/h' : '')
  return (
    <div className={styles.basisCard}>
      <div className={styles.basisHead}>
        <strong>Session {String(r.session_id).slice(0, 8)}</strong>
        <span className={styles.sub}>
          {dmy(r.work_date)} · {r.team_name}
          {r.work_type ? ' · ' + r.work_type : ''}
        </span>
      </div>
      <div className={styles.basisLine}>{line}</div>
      <div className={styles.sub}>{rates}</div>
      {(r.explanation || []).map((s, i) => (
        <div key={i} className={styles.basisWhy}>
          {s}
        </div>
      ))}
      {r.paid_break_minutes > 0 && part && <div className={styles.basisWhy}>{part.sentence}</div>}
      <div className={styles.sub}>
        {r.is_open ? 'Not calculated: session still open' : `Calculated ${day?.calculated_at ? fmtStamp(day.calculated_at) : '—'} · ${r.calculation_version || day?.calculation_version || ''} · ${String(r.id).slice(0, 8)}`}
      </div>
      {stale && <div className={styles.staleNote}>Queued for recalculation</div>}
    </div>
  )
}
